import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import {
  type CryptoPaymentAddress,
  cryptoPaymentAddressDeleteCryptoPaymentAddressMutation,
  cryptoPaymentAddressListCryptoPaymentAddressOptions,
  cryptoPaymentAddressListCryptoPaymentAddressQueryKey,
} from '@column/api';
import { Icon } from '@column/column-ui-kit';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  DataTable,
  type DataTableColumnDef,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Timestamp,
} from '@column/ui';

import { ChainList } from '../Transfers/_components/AccountsSequence/ChainIcon';
import { CryptoPaymentAddressModal } from '../Transfers/_components/AccountsSequence/CryptoPaymentAddressModal';
import { PageActions } from '~/app/Layout/PageFrameContext';
import { isClosedPaymentAddressStatus } from '~/app/pages/Transfers/TypeContent/stablecoin/constants';
import { CopyableValue } from '~/components';
import { LogoLoading } from '~/elements';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { formatDateTime, getFormattedDate } from '~/util';

import { BankAccountSectionProps } from './Section';

// `active`/`deleted` are deprecated aliases of `open`/`closed` still returned for legacy addresses,
// so they share the same accent color.
const getStatusDisplay = (status: string): { color: string; label: string } => {
  switch (status) {
    case 'open':
    case 'active':
      return { color: 'var(--color-seafoam-600)', label: status.charAt(0).toUpperCase() + status.slice(1) };
    case 'closed':
    case 'deleted':
      return { color: 'var(--color-red-600)', label: status.charAt(0).toUpperCase() + status.slice(1) };
    default:
      return { color: 'var(--color-gray-500)', label: status.charAt(0).toUpperCase() + status.slice(1) };
  }
};

export const PageBankAccountsPaymentAddresses: FC = () => {
  const { bankAccount } = useOutletContext<BankAccountSectionProps>();
  const { addDangerNotification } = useNotificationStore();
  const { isAuthorized } = useSessionStore();
  const queryClient = useQueryClient();

  // Creating/closing a payment address mutates the bank account, so gate both on bankAccounts:update
  // (mirrors ACHPositivePay). The API enforces this too; this keeps the UI from offering a no-op.
  const canManage = isAuthorized({
    permission: 'bankAccounts',
    permissionLevel: 'update',
    bankAccount: { id: bankAccount.id, owners: bankAccount.owners },
  });
  const { timeFormat, update: updateTableConfig } = useTableConfig('cryptoPaymentAddresses');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState<CryptoPaymentAddress | null>(null);

  const { data, isLoading } = useQuery(
    cryptoPaymentAddressListCryptoPaymentAddressOptions({ query: { bank_account_id: bankAccount.id } })
  );

  const paymentAddresses = useMemo(() => data?.crypto_payment_addresses ?? [], [data]);

  const handleTimeFormatChange = useCallback(
    (value: TableTimeFormat) => {
      updateTableConfig({ timeFormat: value }).catch(() => {
        addDangerNotification({ content: 'Failed to save time format preference' });
      });
    },
    [updateTableConfig, addDangerNotification]
  );

  const { mutateAsync: deletePaymentAddress, isPending: isDeleting } = useMutation(
    cryptoPaymentAddressDeleteCryptoPaymentAddressMutation()
  );

  const handleDelete = useCallback(async () => {
    if (!addressToDelete) return;
    try {
      await deletePaymentAddress({ path: { crypto_payment_address_id: addressToDelete.id } });
      await queryClient.invalidateQueries({
        queryKey: cryptoPaymentAddressListCryptoPaymentAddressQueryKey({ query: {} }),
      });
      setAddressToDelete(null);
    } catch (error) {
      addDangerNotification({ content: (error as Error).message });
    }
  }, [addressToDelete, deletePaymentAddress, queryClient, addDangerNotification]);

  const columns = useMemo<DataTableColumnDef<CryptoPaymentAddress>[]>(
    () => [
      {
        header: 'Payment Address',
        accessor: 'address',
        width: 'minmax(200px, auto)',
        className: 'min-w-0',
        cell: ({ value }) => <CopyableValue value={value as string} mask={false} monospace />,
      },
      {
        header: 'Network',
        accessor: 'chains',
        width: 'minmax(min-content, 220px)',
        cell: ({ value }) => (
          <ChainList
            chains={(value as string[]) ?? []}
            size={16}
            className="gap-x-5 text-sm font-medium text-foreground"
            itemClassName="gap-1.5"
          />
        ),
      },
      {
        header: 'Status',
        accessor: 'status',
        width: 'minmax(120px, max-content)',
        cell: ({ value }) => {
          const { color, label } = getStatusDisplay(value as string);
          return (
            <span className="flex items-center gap-1.5">
              <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />
              <span className="text-sm font-medium" style={{ color }}>
                {label}
              </span>
            </span>
          );
        },
      },
      {
        accessor: 'created_at',
        width: 'min-content',
        header: (
          <DropdownMenu>
            <DropdownMenuTrigger className="inline-flex items-center gap-1 whitespace-nowrap outline-none">
              Created <Icon.ChevronDown style={{ width: 12, height: 12 }} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="gap-2" onSelect={() => handleTimeFormatChange(TableTimeFormat.Relative)}>
                <Icon.Check
                  className={timeFormat === TableTimeFormat.Relative ? 'opacity-100' : 'opacity-0'}
                  style={{ width: 16, height: 16 }}
                />
                Relative Time
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2" onSelect={() => handleTimeFormatChange(TableTimeFormat.Absolute)}>
                <Icon.Check
                  className={timeFormat === TableTimeFormat.Absolute ? 'opacity-100' : 'opacity-0'}
                  style={{ width: 16, height: 16 }}
                />
                Date / Time
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
        cell: ({ value }) => {
          if (!value) return null;
          return (
            <Timestamp
              date={value as string}
              align="right"
              className="text-xs text-muted-foreground"
              formatHover={(hoverDate) =>
                formatDateTime(hoverDate, { timeZoneFormat: timeFormat === TableTimeFormat.Relative ? 'local' : 'utc' })
              }
            >
              {timeFormat === TableTimeFormat.Relative
                ? getFormattedDate(value as string, 'time-passed')
                : formatDateTime(value as string, { timeZoneFormat: 'local' })}
            </Timestamp>
          );
        },
      },
      {
        header: '',
        accessor: 'id',
        width: 'min-content',
        cell: ({ row }) => {
          if (!canManage || isClosedPaymentAddressStatus(row.status)) return null;
          return (
            <button
              type="button"
              aria-label="Delete payment address"
              onClick={() => setAddressToDelete(row)}
              className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-destructive focus-visible:bg-muted focus-visible:text-destructive focus-visible:outline-none"
            >
              <Icon.Trash style={{ width: 16, height: 16 }} />
            </button>
          );
        },
      },
    ],
    [handleTimeFormatChange, timeFormat, canManage]
  );

  return (
    <>
      {canManage && (
        <PageActions>
          <Button size="sm" onClick={() => setIsModalOpen(true)}>
            <Icon.Plus style={{ width: 16, height: 16 }} />
            Create Payment Address
          </Button>
        </PageActions>
      )}

      <Inner pt="var(--page-margin)" px="var(--page-margin)">
        <DataTable<CryptoPaymentAddress>
          columns={columns}
          data={paymentAddresses}
          rowKey={(row) => row.id}
          isLoading={isLoading}
          loadingContent={<LogoLoading />}
          emptyContent={
            <Empty className="min-h-[300px]">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Icon.Transfer.Stablecoin />
                </EmptyMedia>
                <EmptyTitle>No payment addresses</EmptyTitle>
                <EmptyDescription>
                  Create a payment address to send and receive stablecoins funded by this bank account.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          }
          rowClassName="min-h-11"
        />
      </Inner>

      <CryptoPaymentAddressModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreated={() => setIsModalOpen(false)}
        bankAccountId={bankAccount.id}
      />

      <AlertDialog open={!!addressToDelete} onOpenChange={(open) => !open && !isDeleting && setAddressToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete payment address?</AlertDialogTitle>
            <AlertDialogDescription>
              This closes the payment address and it cannot be reopened. Its transaction history stays visible, but
              on-chain transfers sent to it afterward will be returned to the sender rather than converted to fiat.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel variant="secondary" size="default" disabled={isDeleting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              variant="danger"
              size="default"
              disabled={isDeleting}
              onClick={(event) => {
                event.preventDefault();
                handleDelete();
              }}
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
